import axios, { type AxiosInstance } from "axios";
import { ApiError, AppLogger, failure, success, type Result } from "@utils/index";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const resolveClientBaseUrl = (configuredBaseUrl: string): string => {
    if (import.meta.env.DEV) {
        return "/api";
    }
    return configuredBaseUrl;
};

// ---------------------------------------------------------------------------
// Response / row types  (derived from the NEO Sat OpenAPI 3.1 spec)
// ---------------------------------------------------------------------------

// -- Root / health ----------------------------------------------------------

export type RootResponse = Record<string, string>;
export type HealthResponse = Record<string, string>;

// -- SGS KPIs ---------------------------------------------------------------

export type SgsKpisFilters = {
    readonly month?: number;
    readonly aircraftIds?: string[];
    readonly bypassCache?: boolean;
};

export type SgsKpisResponse = {
    readonly month: number | null;
    readonly aircraft_ids: string[] | null;
    readonly coverage_per_aircraft: Record<string, unknown>[];
    readonly overall_coverage: Record<string, unknown>;
    readonly beam_load_cdf: Record<string, unknown>[];
    readonly below10k_cdf: Record<string, unknown>[];
    readonly beam_kpis: Record<string, unknown>[];
    readonly usage_summary: Record<string, unknown>;
    readonly beam_hourly_occupation: Record<string, unknown>[];
    readonly beam_peak_hours: Record<string, unknown>[];
    readonly cached: boolean;
};

// -- Airlines ---------------------------------------------------------------

export type AirlineRow = {
    readonly airline_iata: string;
    readonly aircraft_count: number;
    readonly skywaves_client_count: number;
    readonly aircraft_types: string[];
    readonly bandwidth_mbs: number | null;
    readonly monthly_price_usd: number | null;
    readonly total_observations: number;
    readonly first_seen: string | null;
    readonly last_seen: string | null;
};

export type AirlineListResponse = {
    readonly data: readonly AirlineRow[];
};

export type AirlineFilters = {
    readonly airlineIata?: string;
};

// -- Satellites -------------------------------------------------------------

export type SatelliteEntry = {
    readonly satellite_id: number;
    readonly satellite_name: string;
};

export type SatelliteListResponse = {
    readonly satellites: readonly SatelliteEntry[];
};

// -- Satellite priority -----------------------------------------------------

export type SatellitePriorityEntry = {
    readonly satellite_id: number;
    readonly priority: number;
};

export type SatellitePriorityResponse = {
    readonly priorities: readonly SatellitePriorityEntry[];
};

// -- Beams ------------------------------------------------------------------

export type BeamSnapshotFilters = {
    readonly beamId?: number;
    readonly satelliteId?: number;
    readonly airlineIata?: string;
    readonly aircraftIcao?: string;
};

export type BeamSnapshotRow = {
    readonly time_of_day: string;
    readonly beam_id: number;
    readonly satellite_id: number;
    readonly aircraft_icao: string;
    readonly avg_aircraft: number;
    readonly avg_above_10k: number;
    readonly avg_below_10k: number;
    readonly bandwidth_mbs: number | null;
    readonly avg_total_bandwidth_mbs: number | null;
};

export type BeamSnapshotResponse = {
    readonly filters: Record<string, unknown>;
    readonly data: readonly BeamSnapshotRow[];
};

// -- Cache stats ------------------------------------------------------------

export type CacheStatsResponse = {
    readonly enabled: boolean;
    readonly config: Record<string, unknown>;
    readonly metrics: Record<string, unknown>;
    readonly entries: Record<string, number>;
    readonly namespace_ttls: Record<string, number>;
    readonly route_ttls: Record<string, number>;
};

// -- Coverage ---------------------------------------------------------------

export type CoverageRow = {
    readonly satellite_id: number;
    readonly in_beam: number;
    readonly no_beam: number;
};

export type CoverageResponse = {
    readonly data: readonly CoverageRow[];
};

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export default class NeoSatApiClient {
    protected readonly _client: AxiosInstance;

    constructor(baseURL: string) {
        this._client = axios.create({
            baseURL: resolveClientBaseUrl(baseURL),
            timeout: 12_000,
            headers: { "Content-Type": "application/json" },
        });
    }

    // -- Root / health ------------------------------------------------------

    public async fetchRoot(): Promise<Result<RootResponse, ApiError>> {
        const endpoint = "/";
        try {
            const response = await this._client.get<RootResponse>(endpoint);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Root request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    public async fetchHealth(): Promise<Result<HealthResponse, ApiError>> {
        const endpoint = "/health";
        try {
            const response = await this._client.get<HealthResponse>(endpoint);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Health check failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- SGS KPIs -----------------------------------------------------------

    public async fetchSgsKpis(filters: SgsKpisFilters = {}): Promise<Result<SgsKpisResponse, ApiError>> {
        const endpoint = "/sgs/kpis";
        try {
            const params = new URLSearchParams();
            if (filters.month !== undefined) {
                params.set("month", String(filters.month));
            }
            if (filters.aircraftIds?.length) {
                for (const id of filters.aircraftIds) {
                    params.append("aircraft_ids", id);
                }
            }
            if (filters.bypassCache) {
                params.set("bypass_cache", "true");
            }
            const response = await this._client.get<SgsKpisResponse>(endpoint, { params, timeout: 30_000 });
            AppLogger.info(`SGS KPIs received (cached=${String(response.data.cached)})`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("SGS KPIs request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- Airlines -----------------------------------------------------------

    public async fetchAirlines(filters: AirlineFilters = {}): Promise<Result<AirlineListResponse, ApiError>> {
        const endpoint = "/airlines";
        try {
            const params = new URLSearchParams();
            if (filters.airlineIata) {
                params.set("airline_iata", filters.airlineIata);
            }
            const response = await this._client.get<AirlineListResponse>(endpoint, { params });
            AppLogger.info(`Airlines received: ${response.data.data.length} rows`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Airlines request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- Satellites ---------------------------------------------------------

    public async fetchSatellites(): Promise<Result<SatelliteListResponse, ApiError>> {
        const endpoint = "/satellites";
        try {
            const response = await this._client.get<SatelliteListResponse>(endpoint);
            AppLogger.info(`Satellites received: ${response.data.satellites.length} entries`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Satellites request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    public async fetchSatellitePriorities(): Promise<Result<SatellitePriorityResponse, ApiError>> {
        const endpoint = "/satellites/priority";
        try {
            const response = await this._client.get<SatellitePriorityResponse>(endpoint);
            AppLogger.info(`Satellite priorities received: ${response.data.priorities.length} entries`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Satellite priorities request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- Beams --------------------------------------------------------------

    private _buildBeamParams(filters: BeamSnapshotFilters): URLSearchParams {
        const params = new URLSearchParams();
        if (filters.beamId !== undefined) {
            params.set("beam_id", String(filters.beamId));
        }
        if (filters.satelliteId !== undefined) {
            params.set("satellite_id", String(filters.satelliteId));
        }
        if (filters.airlineIata) {
            params.set("airline_iata", filters.airlineIata);
        }
        if (filters.aircraftIcao) {
            params.set("aircraft_icao", filters.aircraftIcao);
        }
        return params;
    }

    public async fetchBeamSnapshots(filters: BeamSnapshotFilters = {}): Promise<Result<BeamSnapshotResponse, ApiError>> {
        const endpoint = "/beams";
        try {
            const params = this._buildBeamParams(filters);
            const response = await this._client.get<BeamSnapshotResponse>(endpoint, { params, timeout: 30_000 });
            AppLogger.info(`Beam snapshots received: ${response.data.data.length} rows`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Beam snapshot request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    public async fetchBeamSnapshotsLayered(filters: BeamSnapshotFilters = {}): Promise<Result<BeamSnapshotResponse, ApiError>> {
        const endpoint = "/beams/layered";
        try {
            const params = this._buildBeamParams(filters);
            const response = await this._client.get<BeamSnapshotResponse>(endpoint, { params, timeout: 60_000 });
            AppLogger.info(`Layered beam snapshots received: ${response.data.data.length} rows`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Layered beam snapshot request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- Cache stats --------------------------------------------------------

    public async fetchCacheStats(): Promise<Result<CacheStatsResponse, ApiError>> {
        const endpoint = "/cache/cache/stats";
        try {
            const response = await this._client.get<CacheStatsResponse>(endpoint);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Cache stats request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }

    // -- Coverage -----------------------------------------------------------

    public async fetchCoverage(): Promise<Result<CoverageResponse, ApiError>> {
        const endpoint = "/stats/coverage";
        try {
            const response = await this._client.get<CoverageResponse>(endpoint);
            AppLogger.info(`Coverage received: ${response.data.data.length} rows`);
            return success(response.data);
        } catch (error) {
            AppLogger.error("Coverage request failed:", error);
            return failure(ApiError.fromAxiosError(error, endpoint));
        }
    }
}
